import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useProperty,
  usePropertyEditRights,
  usePropertyPurchasedAddons,
} from "../../hooks/property.hooks";
import { ContentCard } from "../../components/ContentCard";
import { Typography } from "common/components/Typography";
import { PropertyRow } from "../../components/PropertyRow";
import { MarkerMap } from "../../components/map/MarkerMap";
import { Button, Divider, Link } from "@mui/material";
import { PredictionOverview } from "../../components/predictionOverview/PredictionOverview";
import { PropertyImageGallery } from "../../components/imageGallery/PropertyImageGallery";
import { useAppraiser } from "../../hooks/auth.hooks";
import {
  AppraiserRole,
  PropertyRequestStatus,
} from "../../types/appraiser.types";
import { LinkForButton } from "../../components/links/LinkForButton";
import colors from "common/styles/colors";
import { DeleteDialog } from "../../components/dialogs/DeleteDialog";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { deleteAppraiserProperty } from "../../api/appraiserProperties.api";
import { showToastError, showToastSuccess } from "common/toast/toast";
import { PropertyInfoContent } from "./PropertyInfoContent";
import { PropertyLeadContact } from "../../components/properties/PropertyLeadContact";
import { LoaderCentered } from "../../components/loaders/LoaderCentered";
import { BasicAppraiser } from "../propertiesCRUD/BasicAppraiser";
import { PropertyStatuses } from "./PropertyStatuses";
import { PropertyComment } from "./PropertyComment";
import { PropertyAppraiserSet } from "./PropertyAppraiserSet";
import { PredictionCompositeAssets } from "../../components/PredictionCompositeAssets";
import { PropertyFileUpload } from "./PropertyFileUpload";
import { PropertyEditButton } from "./PropertyEditButton";
import {
  getDepositPaid,
  getPropertyPurchasedAddons,
  getPropertyPurchasedCompositeAssets,
} from "../../api/api";
import { PropertyWithGallery } from "./PropertyWithGallery";
import { ExpirationTimeTag } from "../../components/tags/ExpirationTimeTag";
import { PredictionOverviewCard } from "../../components/predictionOverview/PredictionOverviewCard";
import { CompositeAssetsTable } from "../../components/comparablesSummary/CompositeAssetsTable";
import { PropertyCompositeAssets } from "./PropertyCompositeAssets";
import { useTranslation } from "react-i18next";
import { PropertyPortfolioSettings } from "./PropertyPortfolioSettings";
import { DuplicateUniqueNrWarning } from "../../components/properties/DuplicateUniqueNrWarning";

interface PropertyProps {
  propertyId: string;
}

export const Property: React.FC<PropertyProps> = ({ propertyId }) => {
  const { t } = useTranslation();
  const { data: property, isLoading, isError } = useProperty(propertyId);

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const navigate = useNavigate();

  const queryClient = useQueryClient();
  const { mutate: deleteProperty } = useMutation(deleteAppraiserProperty, {
    onSuccess: () => {
      queryClient.invalidateQueries(["appraiserProperties"]);
      navigate("..");
    },
    onError: () => {
      showToastError();
    },
  });

  const { canEdit } = usePropertyEditRights(propertyId);

  const { data: appraiser, isLoading: isLoadingAppraiser } = useAppraiser();

  const { data: addons, isLoading: isLoadingAddons } =
    usePropertyPurchasedAddons(propertyId);

  const {
    data: purchasedCompositeAssets,
    isLoading: isLoadingCompositeAssets,
  } = useQuery(["compositeAssetsPurchased", propertyId], () =>
    getPropertyPurchasedCompositeAssets(propertyId)
  );

  const { data: depositPaid, isLoading: isLoadingDeposits } = useQuery(
    ["depositPaid", propertyId],
    () => getDepositPaid(propertyId)
  );

  const isInProgress =
    property?.propertyRequestStatus !== PropertyRequestStatus.Completed &&
    property?.propertyRequestStatus !== PropertyRequestStatus.Evaluated;

  if (
    isLoading ||
    isLoadingAppraiser ||
    isLoadingAddons ||
    isLoadingCompositeAssets ||
    isLoadingDeposits
  ) {
    return <LoaderCentered />;
  }

  if (isError) {
    return <div>{t("common.error")}</div>;
  }

  if (!property) {
    return null;
  }

  return (
    <PropertyWithGallery property={property}>
      <div style={{ display: "flex", flexDirection: "column" }}>
        {appraiser?.role === AppraiserRole.ADMIN && (
          <>
            <PropertyAppraiserSet property={property} />
            {property.propertyRequestStatus ===
              PropertyRequestStatus.Completed && (
              <PropertyPortfolioSettings property={property} />
            )}
          </>
        )}
        {!!property.leadContact && (
          <PropertyLeadContact
            leadContact={property.leadContact}
            createdAt={property.createdAt.toString()}
            updatedAt={property.updatedAtWithToken}
            discountCode={property.order?.discountCode}
            expectedValue={property.expectedValue}
            addons={addons}
            compositeAssets={purchasedCompositeAssets}
            deliveryTimeLabel={property.order?.deliveryTimeLabel ?? undefined}
            timeLeftBadge={
              !!property.order?.deliveryDate &&
              property.propertyRequestStatus !==
                PropertyRequestStatus.Completed ? (
                <ExpirationTimeTag deliveryDate={property.order.deliveryDate} />
              ) : undefined
            }
            depositPaid={depositPaid?.totalDepositPaid}
            showLinkToMessages
          />
        )}
        <DuplicateUniqueNrWarning
          uniqueNr={property.uniqueNr}
          excludePropertyId={property._id}
          style={{ marginBottom: "10px" }}
        />
        <ContentCard title={t("propertyDetails.propertyInfo")}>
          <PropertyInfoContent property={property} />
        </ContentCard>

        {isInProgress && <PropertyCompositeAssets propertyId={propertyId} />}
        {canEdit && <PropertyEditButton propertyId={property._id} />}
        <PredictionOverviewCard property={property} canEdit={canEdit} />
        <PropertyComment propertyId={propertyId} />
        <PropertyFileUpload propertyId={propertyId} />
        {!!property.leadContact && (
          <PropertyFileUpload
            customHeader={t("propertyDetails.customerUploadedDocuments")}
            isCustomer
            customEditRight={false}
            propertyId={propertyId}
          />
        )}
        {canEdit && (
          <>
            <Link
              color={colors.red}
              style={{ cursor: "pointer", alignSelf: "flex-end" }}
              onClick={() => setIsDeleteOpen(true)}
            >
              {t("common.delete")} {t("propertyDetails.property")}
            </Link>
            <DeleteDialog
              open={isDeleteOpen}
              onCancel={() => setIsDeleteOpen(false)}
              onConfirm={() => deleteProperty(property._id)}
              confirmationText={t("propertyDetails.deleteConfirmation", {
                label: property.label,
              })}
            />
          </>
        )}
      </div>
    </PropertyWithGallery>
  );
};
